import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Heading,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Spinner,
  Center,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Checkbox,
  NumberInput,
  NumberInputField,
  Collapse,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import supabase from '../supabaseClient';
import {
  SPECIALIZATION_TO_SERVICE,
  SERVICE_TYPE_LABELS,
  BILLING_SERVICE_TYPES,
} from '../utils/serviceTypeUtils';
import {
  isRushedProgressReport,
  getProgressReportFees,
  getProgressReportRushedStatus,
  projectRushedFromDueDate,
  PROGRESS_REPORT_STATUSES,
  PROGRESS_REPORT_STATUS_LABELS,
} from '../utils/progressReportUtils';
import { MENTORSHIP_HOURLY_RATE } from '../utils/payrollExtrasUtils';
import { getLocalDateString, parseLocalDateString } from '../utils/dateUtils';
import {
  fetchClientsForSelect,
  isActiveClientStatus,
} from '../utils/clientSelectUtils';
import {
  syncInitialEvaluationSubmissions,
  computeIePenalty,
  IE_STATUSES,
  IE_STATUS_LABELS,
} from '../utils/initialEvaluationUtils';

const toRequestedAtTimestamp = (dateStr) => {
  const date = parseLocalDateString(dateStr);
  date.setHours(12, 0, 0, 0);
  return date.toISOString();
};

const toPaidAtTimestamp = toRequestedAtTimestamp;
const toSubmittedAtTimestamp = toRequestedAtTimestamp;

const timestampToLocalDateString = (timestamp) => {
  if (!timestamp) return '';
  return getLocalDateString(new Date(timestamp));
};

const emptyRequestForm = () => ({
  client_id: '',
  service_type: '',
  therapist_id: '',
  requested_date: getLocalDateString(new Date()),
  due_date: '',
  paid_date: getLocalDateString(new Date()),
  or_number: '',
  receipt_requested: false,
  notes: '',
});

const SERVICE_TO_SPECIALIZATION = Object.fromEntries(
  Object.entries(SPECIALIZATION_TO_SERVICE).map(([spec, code]) => [code, spec])
);
SERVICE_TO_SPECIALIZATION.FT = 'Occupational Therapy';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
  }).format(amount || 0);

const STATUS_COLORS = {
  pending_payment: 'orange',
  paid: 'blue',
  therapist_notified: 'purple',
  submitted: 'green',
};

const IE_STATUS_COLORS = {
  conducted: 'blue',
  therapist_notified: 'purple',
  submitted: 'green',
};

const emptyIeEditForm = () => ({
  conducted_date: '',
  due_date: '',
  notes: '',
});

const InitialEvaluationsTab = () => {
  const toast = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitForm, setSubmitForm] = useState({
    submitted_date: getLocalDateString(new Date()),
  });
  const [submitFile, setSubmitFile] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [removeExistingFile, setRemoveExistingFile] = useState(false);
  const [editForm, setEditForm] = useState(emptyIeEditForm());
  const [isSubmittedExpanded, setIsSubmittedExpanded] = useState(false);

  const loadSubmissions = useCallback(async () => {
    const { data, error } = await supabase
      .from('initial_evaluation_submissions')
      .select(`
        *,
        client:clients (id, name),
        therapist:therapists (id, name, specialization),
        client_document:client_documents (id, file_name, file_path)
      `)
      .order('conducted_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setSubmissions(await loadSubmissions());
    } catch (error) {
      toast({
        title: 'Error loading initial evaluations',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }

    try {
      await syncInitialEvaluationSubmissions(supabase);
      setSubmissions(await loadSubmissions());
    } catch (error) {
      toast({
        title: 'Error syncing initial evaluations',
        description: error.message,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [loadSubmissions, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pendingSubmissions = useMemo(
    () =>
      submissions
        .filter((row) => row.status !== IE_STATUSES.SUBMITTED)
        .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date))),
    [submissions]
  );

  const submittedSubmissions = useMemo(
    () =>
      submissions
        .filter((row) => row.status === IE_STATUSES.SUBMITTED)
        .sort((a, b) => String(b.submitted_at || '').localeCompare(String(a.submitted_at || ''))),
    [submissions]
  );

  const openEditModal = (submission) => {
    setSelectedSubmission(submission);
    setEditForm({
      conducted_date: String(submission.conducted_at).slice(0, 10),
      due_date: String(submission.due_date).slice(0, 10),
      notes: submission.notes || '',
    });
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedSubmission || !editForm.conducted_date || !editForm.due_date) {
      toast({
        title: 'Missing fields',
        description: 'Conducted and due dates are required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('initial_evaluation_submissions')
        .update({
          conducted_at: editForm.conducted_date,
          due_date: editForm.due_date,
          notes: editForm.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedSubmission.id);

      if (error) throw error;
      toast({ title: 'Initial evaluation updated', status: 'success', duration: 3000 });
      setIsEditOpen(false);
      setSelectedSubmission(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetSubmitModal = () => {
    setSubmitForm({ submitted_date: getLocalDateString(new Date()) });
    setSubmitFile(null);
    setExistingDocument(null);
    setRemoveExistingFile(false);
  };

  const openSubmitModal = (submission) => {
    setSelectedSubmission(submission);
    setSubmitForm({
      submitted_date:
        timestampToLocalDateString(submission.submitted_at) || getLocalDateString(new Date()),
    });
    setSubmitFile(null);
    setExistingDocument(submission.client_document || null);
    setRemoveExistingFile(false);
    setIsSubmitOpen(true);
  };

  const handleSubmitEvaluation = async () => {
    if (!selectedSubmission) return;

    if (!submitForm.submitted_date) {
      toast({
        title: 'Missing date',
        description: 'Please enter the date the evaluation was submitted',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const clientId = selectedSubmission.client_id;
      const submittedAt = toSubmittedAtTimestamp(submitForm.submitted_date);
      const submittedDateLabel = parseLocalDateString(submitForm.submitted_date).toLocaleDateString();
      const isEditingSubmission = selectedSubmission.status === IE_STATUSES.SUBMITTED;
      let clientDocumentId = selectedSubmission.client_document_id || null;

      if (removeExistingFile && existingDocument) {
        const { error: deleteDocError } = await supabase
          .from('client_documents')
          .delete()
          .eq('id', existingDocument.id);

        if (deleteDocError) throw deleteDocError;
        clientDocumentId = null;
      }

      if (submitFile) {
        if (existingDocument && !removeExistingFile) {
          const { error: replaceDeleteError } = await supabase
            .from('client_documents')
            .delete()
            .eq('id', existingDocument.id);

          if (replaceDeleteError) throw replaceDeleteError;
        }

        const fileExt = submitFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${clientId}/evaluations/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('client-documents')
          .upload(filePath, submitFile);

        if (uploadError) throw uploadError;

        const { data: docData, error: docError } = await supabase
          .from('client_documents')
          .insert({
            client_id: clientId,
            document_category: 'Evaluations',
            document_type: 'Initial Evaluation',
            title: `Initial Evaluation - ${SERVICE_TYPE_LABELS[selectedSubmission.service_type] || selectedSubmission.service_type}`,
            description: `Initial evaluation submitted ${submittedDateLabel}`,
            file_name: submitFile.name,
            file_path: filePath,
            file_size: submitFile.size,
            mime_type: submitFile.type,
          })
          .select('id')
          .single();

        if (docError) throw docError;
        clientDocumentId = docData.id;
      } else if (!removeExistingFile && existingDocument && isEditingSubmission) {
        const { error: docUpdateError } = await supabase
          .from('client_documents')
          .update({
            description: `Initial evaluation submitted ${submittedDateLabel}`,
          })
          .eq('id', existingDocument.id);

        if (docUpdateError) throw docUpdateError;
      }

      const { error: updateError } = await supabase
        .from('initial_evaluation_submissions')
        .update({
          status: IE_STATUSES.SUBMITTED,
          submitted_at: submittedAt,
          client_document_id: clientDocumentId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedSubmission.id);

      if (updateError) throw updateError;

      toast({
        title: isEditingSubmission ? 'Submission updated' : 'Initial evaluation submitted',
        status: 'success',
        duration: 3000,
      });
      setIsSubmitOpen(false);
      setSelectedSubmission(null);
      resetSubmitModal();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSubmissionRow = (submission, showSubmittedColumn) => {
    const referenceDate =
      submission.status === IE_STATUSES.SUBMITTED && submission.submitted_at
        ? timestampToLocalDateString(submission.submitted_at)
        : getLocalDateString(new Date());
    const penalty = computeIePenalty(submission.due_date, referenceDate);
    const isOverdue = submission.status !== IE_STATUSES.SUBMITTED && penalty > 0;

    return (
      <Tr key={submission.id} bg={isOverdue ? 'orange.50' : undefined}>
        <Td>{submission.client?.name}</Td>
        <Td>{SERVICE_TYPE_LABELS[submission.service_type] || submission.service_type}</Td>
        <Td>{submission.therapist?.name}</Td>
        <Td>{new Date(submission.conducted_at).toLocaleDateString()}</Td>
        <Td>{new Date(submission.due_date).toLocaleDateString()}</Td>
        <Td>
          {showSubmittedColumn && submission.submitted_at
            ? new Date(submission.submitted_at).toLocaleDateString()
            : '—'}
        </Td>
        <Td>
          {penalty > 0 ? (
            <Text color="red.600" fontWeight="medium">
              {formatCurrency(penalty)}
            </Text>
          ) : (
            formatCurrency(0)
          )}
        </Td>
        <Td>
          <Badge colorScheme={IE_STATUS_COLORS[submission.status] || 'gray'}>
            {IE_STATUS_LABELS[submission.status]}
          </Badge>
        </Td>
        <Td>
          <HStack spacing={1} flexWrap="wrap">
            <IconButton
              aria-label="Edit initial evaluation"
              icon={<EditIcon />}
              size="xs"
              variant="ghost"
              onClick={() => openEditModal(submission)}
            />
            {submission.status !== IE_STATUSES.SUBMITTED && (
              <Button size="xs" colorScheme="purple" onClick={() => openSubmitModal(submission)}>
                Submit Evaluation
              </Button>
            )}
            {submission.status === IE_STATUSES.SUBMITTED && (
              <Button
                size="xs"
                variant="outline"
                colorScheme="purple"
                onClick={() => openSubmitModal(submission)}
              >
                Edit Submission
              </Button>
            )}
          </HStack>
        </Td>
      </Tr>
    );
  };

  const renderTable = (rows, { heading, emptyMessage, showSubmittedColumn, count, countColorScheme = 'gray' }) => (
    <Box>
      {heading && (
        <HStack spacing={2} mb={3}>
          <Heading size="sm">{heading}</Heading>
          {count !== undefined && (
            <Badge colorScheme={countColorScheme}>{count}</Badge>
          )}
        </HStack>
      )}
      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Client</Th>
              <Th>Therapy</Th>
              <Th>Therapist</Th>
              <Th>Conducted</Th>
              <Th>Due</Th>
              <Th>Submitted</Th>
              <Th>Penalty</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => renderSubmissionRow(row, showSubmittedColumn))}
            {rows.length === 0 && (
              <Tr>
                <Td colSpan={9} textAlign="center" color="gray.500">
                  {emptyMessage}
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Heading size="md">Initial Evaluations</Heading>

      {renderTable(pendingSubmissions, {
        heading: 'Pending Submission',
        count: pendingSubmissions.length,
        countColorScheme: 'orange',
        emptyMessage: 'No pending initial evaluations',
        showSubmittedColumn: false,
      })}

      <Box>
        <HStack
          as="button"
          type="button"
          onClick={() => setIsSubmittedExpanded((expanded) => !expanded)}
          spacing={2}
          mb={3}
          w="100%"
          textAlign="left"
          _hover={{ opacity: 0.85 }}
        >
          {isSubmittedExpanded ? <ChevronUpIcon boxSize={4} /> : <ChevronDownIcon boxSize={4} />}
          <Heading size="sm">Submitted</Heading>
          <Badge colorScheme="green">{submittedSubmissions.length}</Badge>
        </HStack>
        <Collapse in={isSubmittedExpanded} animateOpacity>
          {renderTable(submittedSubmissions, {
            heading: null,
            emptyMessage: 'No submitted initial evaluations',
            showSubmittedColumn: true,
          })}
        </Collapse>
      </Box>

      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Initial Evaluation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Conducted Date</FormLabel>
                <Input
                  type="date"
                  value={editForm.conducted_date}
                  onChange={(e) =>
                    setEditForm({ ...editForm, conducted_date: e.target.value })
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Due Date</FormLabel>
                <Input
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveEdit} isLoading={isSubmitting}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isSubmitOpen}
        onClose={() => {
          setIsSubmitOpen(false);
          setSelectedSubmission(null);
          resetSubmitModal();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedSubmission?.status === IE_STATUSES.SUBMITTED
              ? 'Edit Initial Evaluation Submission'
              : 'Submit Initial Evaluation'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Optionally upload the evaluation to the client&apos;s record (visible in parent portal under Evaluations).
              </Text>
              <FormControl isRequired>
                <FormLabel>Date Submitted</FormLabel>
                <Input
                  type="date"
                  value={submitForm.submitted_date}
                  onChange={(e) =>
                    setSubmitForm({ ...submitForm, submitted_date: e.target.value })
                  }
                />
              </FormControl>
              {existingDocument && !removeExistingFile && (
                <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                  <Text fontSize="sm" fontWeight="medium">
                    Current file: {existingDocument.file_name}
                  </Text>
                  <Checkbox
                    mt={2}
                    isChecked={removeExistingFile}
                    onChange={(e) => {
                      setRemoveExistingFile(e.target.checked);
                      if (e.target.checked) {
                        setSubmitFile(null);
                      }
                    }}
                  >
                    Remove current file
                  </Checkbox>
                </Box>
              )}
              {removeExistingFile && existingDocument && (
                <Text fontSize="sm" color="orange.600">
                  The current file will be removed from the client record.
                </Text>
              )}
              <FormControl>
                <FormLabel>
                  {existingDocument && !removeExistingFile
                    ? 'Replace Evaluation File (optional)'
                    : 'Evaluation File (optional)'}
                </FormLabel>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setSubmitFile(e.target.files?.[0] || null)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setIsSubmitOpen(false);
                setSelectedSubmission(null);
                resetSubmitModal();
              }}
            >
              Cancel
            </Button>
            <Button colorScheme="purple" onClick={handleSubmitEvaluation} isLoading={isSubmitting}>
              {selectedSubmission?.status === IE_STATUSES.SUBMITTED ? 'Save Changes' : 'Submit'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

const ProgressReportsTab = () => {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const [isRequestFormOpen, setIsRequestFormOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [requestForm, setRequestForm] = useState(emptyRequestForm());

  const [paymentForm, setPaymentForm] = useState({
    paid_date: getLocalDateString(new Date()),
    or_number: '',
    receipt_requested: false,
  });

  const [submitForm, setSubmitForm] = useState({
    submitted_date: getLocalDateString(new Date()),
  });
  const [submitFile, setSubmitFile] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [removeExistingFile, setRemoveExistingFile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittedExpanded, setIsSubmittedExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [requestsRes, clientsData, therapistsRes] = await Promise.all([
        supabase
          .from('progress_report_requests')
          .select(`
            *,
            client:clients (id, name),
            therapist:therapists (id, name, specialization),
            client_document:client_documents (id, file_name, file_path)
          `)
          .order('due_date', { ascending: true }),
        fetchClientsForSelect(supabase),
        supabase
          .from('therapists')
          .select('id, name, specialization')
          .eq('status', 'active')
          .order('name'),
      ]);

      if (requestsRes.error) throw requestsRes.error;
      if (therapistsRes.error) throw therapistsRes.error;

      setRequests(requestsRes.data || []);
      setClients(clientsData || []);
      setTherapists(therapistsRes.data || []);
    } catch (error) {
      toast({
        title: 'Error loading progress reports',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredTherapists = useMemo(() => {
    if (!requestForm.service_type) return therapists;
    const spec = SERVICE_TO_SPECIALIZATION[requestForm.service_type];
    return therapists.filter((t) => t.specialization === spec);
  }, [therapists, requestForm.service_type]);

  const activeClients = useMemo(
    () => clients.filter((client) => isActiveClientStatus(client.status)),
    [clients]
  );

  const inactiveClients = useMemo(
    () => clients.filter((client) => !isActiveClientStatus(client.status)),
    [clients]
  );

  const showPaymentFieldsInForm =
    editingRequest &&
    editingRequest.status !== PROGRESS_REPORT_STATUSES.PENDING_PAYMENT;

  const openCreateModal = () => {
    setEditingRequest(null);
    setRequestForm(emptyRequestForm());
    setIsRequestFormOpen(true);
  };

  const openEditModal = (request) => {
    setEditingRequest(request);
    setRequestForm({
      client_id: request.client_id,
      service_type: request.service_type,
      therapist_id: request.therapist_id,
      requested_date: timestampToLocalDateString(request.requested_at),
      due_date: String(request.due_date).slice(0, 10),
      paid_date: timestampToLocalDateString(request.paid_at) || getLocalDateString(new Date()),
      or_number: request.or_number || '',
      receipt_requested: Boolean(request.receipt_requested),
      notes: request.notes || '',
    });
    setIsRequestFormOpen(true);
  };

  const closeRequestFormModal = () => {
    setIsRequestFormOpen(false);
    setEditingRequest(null);
  };

  const pendingRequests = useMemo(() => {
    let list = requests.filter((r) => r.status !== PROGRESS_REPORT_STATUSES.SUBMITTED);
    if (statusFilter !== 'all') {
      list = list.filter((r) => r.status === statusFilter);
    }
    return list.sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  }, [requests, statusFilter]);

  const submittedRequests = useMemo(() => {
    if (statusFilter !== 'all' && statusFilter !== PROGRESS_REPORT_STATUSES.SUBMITTED) {
      return [];
    }
    return requests
      .filter((r) => r.status === PROGRESS_REPORT_STATUSES.SUBMITTED)
      .sort((a, b) =>
        String(b.submitted_at || b.due_date).localeCompare(String(a.submitted_at || a.due_date))
      );
  }, [requests, statusFilter]);

  const handleSaveRequest = async () => {
    if (!requestForm.client_id || !requestForm.service_type || !requestForm.therapist_id || !requestForm.requested_date || !requestForm.due_date) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in client, therapy type, therapist, date requested, and due date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (showPaymentFieldsInForm && !requestForm.paid_date) {
      toast({
        title: 'Missing date',
        description: 'Please enter the payment date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        client_id: requestForm.client_id,
        therapist_id: requestForm.therapist_id,
        service_type: requestForm.service_type,
        requested_at: toRequestedAtTimestamp(requestForm.requested_date),
        due_date: requestForm.due_date,
        notes: requestForm.notes || null,
        updated_at: new Date().toISOString(),
      };

      if (!editingRequest) {
        const isRushed = projectRushedFromDueDate(
          requestForm.due_date,
          requestForm.requested_date
        );
        const fees = getProgressReportFees(isRushed);

        const { error } = await supabase.from('progress_report_requests').insert([
          {
            ...payload,
            status: PROGRESS_REPORT_STATUSES.PENDING_PAYMENT,
            client_fee: fees.client,
            therapist_fee: fees.therapist,
          },
        ]);

        if (error) throw error;
        toast({ title: 'Progress report request created', status: 'success', duration: 3000 });
      } else {
        if (editingRequest.status === PROGRESS_REPORT_STATUSES.PENDING_PAYMENT) {
          const isRushed = projectRushedFromDueDate(
            requestForm.due_date,
            requestForm.requested_date
          );
          const fees = getProgressReportFees(isRushed);
          payload.client_fee = fees.client;
          payload.therapist_fee = fees.therapist;
          payload.is_rushed = false;
        } else {
          const paidAt = toPaidAtTimestamp(requestForm.paid_date);
          const isRushed = isRushedProgressReport(requestForm.due_date, paidAt);
          const fees = getProgressReportFees(isRushed);
          payload.paid_at = paidAt;
          payload.is_rushed = isRushed;
          payload.client_fee = fees.client;
          payload.therapist_fee = fees.therapist;
          payload.or_number = requestForm.or_number || null;
          payload.receipt_requested = requestForm.receipt_requested;
        }

        const { error } = await supabase
          .from('progress_report_requests')
          .update(payload)
          .eq('id', editingRequest.id);

        if (error) throw error;
        toast({ title: 'Progress report request updated', status: 'success', duration: 3000 });
      }

      closeRequestFormModal();
      setRequestForm(emptyRequestForm());
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPaymentModal = (request) => {
    setSelectedRequest(request);
    setPaymentForm({
      paid_date: getLocalDateString(new Date()),
      or_number: '',
      receipt_requested: false,
    });
    setIsPaymentOpen(true);
  };

  const handlePayment = async () => {
    if (!selectedRequest) return;

    if (!paymentForm.paid_date) {
      toast({
        title: 'Missing date',
        description: 'Please enter the payment date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const paidAt = toPaidAtTimestamp(paymentForm.paid_date);
      const isRushed = isRushedProgressReport(selectedRequest.due_date, paidAt);
      const fees = getProgressReportFees(isRushed);

      const { error } = await supabase
        .from('progress_report_requests')
        .update({
          status: PROGRESS_REPORT_STATUSES.PAID,
          paid_at: paidAt,
          is_rushed: isRushed,
          client_fee: fees.client,
          therapist_fee: fees.therapist,
          or_number: paymentForm.or_number || null,
          receipt_requested: paymentForm.receipt_requested,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedRequest.id);

      if (error) throw error;

      toast({ title: 'Payment recorded', status: 'success', duration: 3000 });
      setIsPaymentOpen(false);
      setSelectedRequest(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkNotified = async (request) => {
    try {
      const { error } = await supabase
        .from('progress_report_requests')
        .update({
          status: PROGRESS_REPORT_STATUSES.THERAPIST_NOTIFIED,
          updated_at: new Date().toISOString(),
        })
        .eq('id', request.id);

      if (error) throw error;
      toast({ title: 'Therapist marked as notified', status: 'success', duration: 3000 });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    }
  };

  const resetSubmitModal = () => {
    setSubmitForm({ submitted_date: getLocalDateString(new Date()) });
    setSubmitFile(null);
    setExistingDocument(null);
    setRemoveExistingFile(false);
  };

  const openSubmitModal = (request) => {
    setSelectedRequest(request);
    setSubmitForm({
      submitted_date:
        timestampToLocalDateString(request.submitted_at) || getLocalDateString(new Date()),
    });
    setSubmitFile(null);
    setExistingDocument(request.client_document || null);
    setRemoveExistingFile(false);
    setIsSubmitOpen(true);
  };

  const handleSubmitReport = async () => {
    if (!selectedRequest) return;

    if (!submitForm.submitted_date) {
      toast({
        title: 'Missing date',
        description: 'Please enter the date the report was submitted',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const clientId = selectedRequest.client_id;
      const submittedAt = toSubmittedAtTimestamp(submitForm.submitted_date);
      const submittedDateLabel = parseLocalDateString(submitForm.submitted_date).toLocaleDateString();
      const isEditingSubmission = selectedRequest.status === PROGRESS_REPORT_STATUSES.SUBMITTED;
      let clientDocumentId = selectedRequest.client_document_id || null;

      if (removeExistingFile && existingDocument) {
        const { error: deleteDocError } = await supabase
          .from('client_documents')
          .delete()
          .eq('id', existingDocument.id);

        if (deleteDocError) throw deleteDocError;
        clientDocumentId = null;
      }

      if (submitFile) {
        if (existingDocument && !removeExistingFile) {
          const { error: replaceDeleteError } = await supabase
            .from('client_documents')
            .delete()
            .eq('id', existingDocument.id);

          if (replaceDeleteError) throw replaceDeleteError;
        }

        const fileExt = submitFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${clientId}/evaluations/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('client-documents')
          .upload(filePath, submitFile);

        if (uploadError) throw uploadError;

        const { data: docData, error: docError } = await supabase
          .from('client_documents')
          .insert({
            client_id: clientId,
            document_category: 'Evaluations',
            document_type: 'Progress Report',
            title: `Progress Report - ${SERVICE_TYPE_LABELS[selectedRequest.service_type] || selectedRequest.service_type}`,
            description: `Progress report submitted ${submittedDateLabel}`,
            file_name: submitFile.name,
            file_path: filePath,
            file_size: submitFile.size,
            mime_type: submitFile.type,
          })
          .select('id')
          .single();

        if (docError) throw docError;
        clientDocumentId = docData.id;
      } else if (
        !removeExistingFile &&
        existingDocument &&
        isEditingSubmission
      ) {
        const { error: docUpdateError } = await supabase
          .from('client_documents')
          .update({
            description: `Progress report submitted ${submittedDateLabel}`,
          })
          .eq('id', existingDocument.id);

        if (docUpdateError) throw docUpdateError;
      }

      const { error: updateError } = await supabase
        .from('progress_report_requests')
        .update({
          status: PROGRESS_REPORT_STATUSES.SUBMITTED,
          submitted_at: submittedAt,
          client_document_id: clientDocumentId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedRequest.id);

      if (updateError) throw updateError;

      toast({
        title: isEditingSubmission ? 'Submission updated' : 'Progress report submitted',
        status: 'success',
        duration: 3000,
      });
      setIsSubmitOpen(false);
      setSelectedRequest(null);
      resetSubmitModal();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleReceiptIssued = async (request) => {
    try {
      const { error } = await supabase
        .from('progress_report_requests')
        .update({
          receipt_issued: !request.receipt_issued,
          updated_at: new Date().toISOString(),
        })
        .eq('id', request.id);

      if (error) throw error;
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    }
  };

  const renderProgressReportRow = (request) => {
    const projectedRushed = getProgressReportRushedStatus(request);

    return (
      <Tr key={request.id}>
        <Td>{request.client?.name}</Td>
        <Td>{SERVICE_TYPE_LABELS[request.service_type] || request.service_type}</Td>
        <Td>{request.therapist?.name}</Td>
        <Td>{new Date(request.requested_at).toLocaleDateString()}</Td>
        <Td>{new Date(request.due_date).toLocaleDateString()}</Td>
        <Td>
          <Badge colorScheme={projectedRushed ? 'red' : 'green'}>
            {projectedRushed ? 'Rushed' : 'Regular'}
          </Badge>
        </Td>
        <Td>{formatCurrency(request.client_fee)}</Td>
        <Td>
          <Badge colorScheme={STATUS_COLORS[request.status] || 'gray'}>
            {PROGRESS_REPORT_STATUS_LABELS[request.status]}
          </Badge>
        </Td>
        <Td>
          <HStack spacing={1} flexWrap="wrap">
            <IconButton
              aria-label="Edit request"
              icon={<EditIcon />}
              size="xs"
              variant="ghost"
              onClick={() => openEditModal(request)}
            />
            {request.status === PROGRESS_REPORT_STATUSES.PENDING_PAYMENT && (
              <Button size="xs" colorScheme="green" onClick={() => openPaymentModal(request)}>
                Record Payment
              </Button>
            )}
            {request.status === PROGRESS_REPORT_STATUSES.PAID && (
              <Button size="xs" onClick={() => handleMarkNotified(request)}>
                Mark Notified
              </Button>
            )}
            {request.status === PROGRESS_REPORT_STATUSES.THERAPIST_NOTIFIED && (
              <Button size="xs" colorScheme="purple" onClick={() => openSubmitModal(request)}>
                Submit Report
              </Button>
            )}
            {request.status === PROGRESS_REPORT_STATUSES.SUBMITTED && (
              <Button size="xs" variant="outline" colorScheme="purple" onClick={() => openSubmitModal(request)}>
                Edit Submission
              </Button>
            )}
            {request.receipt_requested && request.status !== PROGRESS_REPORT_STATUSES.PENDING_PAYMENT && (
              <Button
                size="xs"
                variant={request.receipt_issued ? 'solid' : 'outline'}
                colorScheme={request.receipt_issued ? 'green' : 'gray'}
                onClick={() => handleToggleReceiptIssued(request)}
              >
                {request.receipt_issued ? 'Receipt Issued' : 'Issue Receipt'}
              </Button>
            )}
          </HStack>
        </Td>
      </Tr>
    );
  };

  const renderProgressReportTable = (rows, emptyMessage) => (
    <Box overflowX="auto">
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Client</Th>
            <Th>Therapy</Th>
            <Th>Therapist</Th>
            <Th>Requested</Th>
            <Th>Due</Th>
            <Th>Rate</Th>
            <Th>Client Fee</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((request) => renderProgressReportRow(request))}
          {rows.length === 0 && (
            <Tr>
              <Td colSpan={9} textAlign="center" color="gray.500">
                {emptyMessage}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" />
      </Center>
    );
  }

  const showSubmittedSection =
    statusFilter === 'all' || statusFilter === PROGRESS_REPORT_STATUSES.SUBMITTED;

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
        <Heading size="md">Progress Reports</Heading>
        <HStack spacing={3} flexShrink={0}>
          <Select
            size="sm"
            minW="160px"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {Object.entries(PROGRESS_REPORT_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
          <Button
            leftIcon={<AddIcon boxSize={3} />}
            colorScheme="blue"
            size="sm"
            px={6}
            minW="fit-content"
            flexShrink={0}
            whiteSpace="nowrap"
            iconSpacing={2}
            fontSize="sm"
            onClick={openCreateModal}
          >
            New Request
          </Button>
        </HStack>
      </HStack>

      <Box>
        <HStack spacing={2} mb={3}>
          <Heading size="sm">Pending</Heading>
          <Badge colorScheme="orange">{pendingRequests.length}</Badge>
        </HStack>
        {renderProgressReportTable(pendingRequests, 'No pending progress report requests')}
      </Box>

      {showSubmittedSection && (
        <Box>
          <HStack
            as="button"
            type="button"
            onClick={() => setIsSubmittedExpanded((expanded) => !expanded)}
            spacing={2}
            mb={3}
            w="100%"
            textAlign="left"
            _hover={{ opacity: 0.85 }}
          >
            {isSubmittedExpanded ? <ChevronUpIcon boxSize={4} /> : <ChevronDownIcon boxSize={4} />}
            <Heading size="sm">Submitted</Heading>
            <Badge colorScheme="green">{submittedRequests.length}</Badge>
          </HStack>
          <Collapse in={isSubmittedExpanded} animateOpacity>
            {renderProgressReportTable(submittedRequests, 'No submitted progress report requests')}
          </Collapse>
        </Box>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={isRequestFormOpen} onClose={closeRequestFormModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingRequest ? 'Edit Progress Report Request' : 'New Progress Report Request'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Client</FormLabel>
                <Select
                  placeholder="Select client"
                  value={requestForm.client_id}
                  onChange={(e) => setRequestForm({ ...requestForm, client_id: e.target.value })}
                >
                  <optgroup label="Active clients">
                    {activeClients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </optgroup>
                  {inactiveClients.length > 0 && (
                    <optgroup label="Inactive clients">
                      {inactiveClients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} (Inactive)
                        </option>
                      ))}
                    </optgroup>
                  )}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Therapy Type</FormLabel>
                <Select
                  placeholder="Select therapy type"
                  value={requestForm.service_type}
                  onChange={(e) =>
                    setRequestForm({ ...requestForm, service_type: e.target.value, therapist_id: '' })
                  }
                >
                  {BILLING_SERVICE_TYPES.map((code) => (
                    <option key={code} value={code}>{SERVICE_TYPE_LABELS[code]}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Therapist</FormLabel>
                <Select
                  placeholder="Select therapist"
                  value={requestForm.therapist_id}
                  onChange={(e) => setRequestForm({ ...requestForm, therapist_id: e.target.value })}
                  isDisabled={!requestForm.service_type}
                >
                  {filteredTherapists.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Date Requested</FormLabel>
                <Input
                  type="date"
                  value={requestForm.requested_date}
                  onChange={(e) => setRequestForm({ ...requestForm, requested_date: e.target.value })}
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Use the actual request date if logging this after the fact.
                </Text>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Due Date</FormLabel>
                <Input
                  type="date"
                  value={requestForm.due_date}
                  onChange={(e) => setRequestForm({ ...requestForm, due_date: e.target.value })}
                />
                {requestForm.due_date && !showPaymentFieldsInForm && (
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Projected rate:{' '}
                    {projectRushedFromDueDate(
                      requestForm.due_date,
                      requestForm.requested_date || new Date()
                    ) ? 'Rushed (₱1,260)' : 'Regular (₱840)'}
                    {' '}(final rate determined at payment)
                  </Text>
                )}
              </FormControl>
              {showPaymentFieldsInForm && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Date Paid</FormLabel>
                    <Input
                      type="date"
                      value={requestForm.paid_date}
                      onChange={(e) => setRequestForm({ ...requestForm, paid_date: e.target.value })}
                    />
                  </FormControl>
                  {requestForm.paid_date && (
                    <Text fontSize="sm">
                      Rate:{' '}
                      <Badge colorScheme={isRushedProgressReport(requestForm.due_date, requestForm.paid_date) ? 'red' : 'green'}>
                        {isRushedProgressReport(requestForm.due_date, requestForm.paid_date) ? 'Rushed' : 'Regular'}
                      </Badge>
                      {' — '}
                      {formatCurrency(
                        getProgressReportFees(
                          isRushedProgressReport(requestForm.due_date, requestForm.paid_date)
                        ).client
                      )}
                      {' client / '}
                      {formatCurrency(
                        getProgressReportFees(
                          isRushedProgressReport(requestForm.due_date, requestForm.paid_date)
                        ).therapist
                      )}
                      {' therapist'}
                    </Text>
                  )}
                  <FormControl>
                    <FormLabel>OR Number (Center Receipt)</FormLabel>
                    <Input
                      value={requestForm.or_number}
                      onChange={(e) => setRequestForm({ ...requestForm, or_number: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <Checkbox
                      isChecked={requestForm.receipt_requested}
                      onChange={(e) =>
                        setRequestForm({ ...requestForm, receipt_requested: e.target.checked })
                      }
                    >
                      Client requested receipt
                    </Checkbox>
                  </FormControl>
                </>
              )}
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeRequestFormModal}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSaveRequest} isLoading={isSubmitting}>
              {editingRequest ? 'Save Changes' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Record Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRequest && (
              <VStack spacing={4} align="stretch">
                <Text>
                  Client: <strong>{selectedRequest.client?.name}</strong>
                </Text>
                <Text>
                  Due: {new Date(selectedRequest.due_date).toLocaleDateString()}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Day count for therapist starts upon payment.
                </Text>
                <FormControl isRequired>
                  <FormLabel>Date Paid</FormLabel>
                  <Input
                    type="date"
                    value={paymentForm.paid_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })}
                  />
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Use the actual payment date if recording this after the fact.
                  </Text>
                </FormControl>
                {paymentForm.paid_date && (
                  <Text fontSize="sm">
                    Rate:{' '}
                    <Badge colorScheme={isRushedProgressReport(selectedRequest.due_date, paymentForm.paid_date) ? 'red' : 'green'}>
                      {isRushedProgressReport(selectedRequest.due_date, paymentForm.paid_date) ? 'Rushed' : 'Regular'}
                    </Badge>
                    {' — '}
                    {formatCurrency(
                      getProgressReportFees(
                        isRushedProgressReport(selectedRequest.due_date, paymentForm.paid_date)
                      ).client
                    )}
                    {' client / '}
                    {formatCurrency(
                      getProgressReportFees(
                        isRushedProgressReport(selectedRequest.due_date, paymentForm.paid_date)
                      ).therapist
                    )}
                    {' therapist'}
                  </Text>
                )}
                <FormControl>
                  <FormLabel>OR Number (Center Receipt)</FormLabel>
                  <Input
                    value={paymentForm.or_number}
                    onChange={(e) => setPaymentForm({ ...paymentForm, or_number: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <Checkbox
                    isChecked={paymentForm.receipt_requested}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, receipt_requested: e.target.checked })
                    }
                  >
                    Client requested receipt
                  </Checkbox>
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsPaymentOpen(false)}>Cancel</Button>
            <Button colorScheme="green" onClick={handlePayment} isLoading={isSubmitting}>
              Confirm Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Submit Modal */}
      <Modal
        isOpen={isSubmitOpen}
        onClose={() => {
          setIsSubmitOpen(false);
          setSelectedRequest(null);
          resetSubmitModal();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedRequest?.status === PROGRESS_REPORT_STATUSES.SUBMITTED
              ? 'Edit Progress Report Submission'
              : 'Submit Progress Report'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Optionally upload the report to the client&apos;s record (visible in parent portal under Evaluations).
              </Text>
              <FormControl isRequired>
                <FormLabel>Date Submitted</FormLabel>
                <Input
                  type="date"
                  value={submitForm.submitted_date}
                  onChange={(e) =>
                    setSubmitForm({ ...submitForm, submitted_date: e.target.value })
                  }
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Use the actual submission date if recording this after the fact.
                </Text>
              </FormControl>
              {existingDocument && !removeExistingFile && (
                <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                  <Text fontSize="sm" fontWeight="medium">
                    Current file: {existingDocument.file_name}
                  </Text>
                  <Checkbox
                    mt={2}
                    isChecked={removeExistingFile}
                    onChange={(e) => {
                      setRemoveExistingFile(e.target.checked);
                      if (e.target.checked) {
                        setSubmitFile(null);
                      }
                    }}
                  >
                    Remove current file
                  </Checkbox>
                </Box>
              )}
              {removeExistingFile && existingDocument && (
                <Text fontSize="sm" color="orange.600">
                  The current file will be removed from the client record.
                </Text>
              )}
              <FormControl>
                <FormLabel>
                  {existingDocument && !removeExistingFile ? 'Replace Report File (optional)' : 'Report File (optional)'}
                </FormLabel>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setSubmitFile(e.target.files?.[0] || null)}
                />
                {existingDocument && !removeExistingFile && (
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Upload a new file to replace the current one, or leave blank to keep it.
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setIsSubmitOpen(false);
                setSelectedRequest(null);
                resetSubmitModal();
              }}
            >
              Cancel
            </Button>
            <Button colorScheme="purple" onClick={handleSubmitReport} isLoading={isSubmitting}>
              {selectedRequest?.status === PROGRESS_REPORT_STATUSES.SUBMITTED ? 'Save Changes' : 'Submit'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

const MentorshipTab = () => {
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emptyForm = {
    mentor_therapist_id: '',
    mentee_therapist_id: '',
    session_date: '',
    session_time: '',
    hours: '1',
    notes: '',
  };

  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [sessionsRes, therapistsRes] = await Promise.all([
        supabase
          .from('mentorship_sessions')
          .select(`
            *,
            mentor:mentor_therapist_id (id, name),
            mentee:mentee_therapist_id (id, name)
          `)
          .order('session_date', { ascending: false }),
        supabase
          .from('therapists')
          .select('id, name')
          .eq('status', 'active')
          .order('name'),
      ]);

      if (sessionsRes.error) throw sessionsRes.error;
      if (therapistsRes.error) throw therapistsRes.error;

      setSessions(sessionsRes.data || []);
      setTherapists(therapistsRes.data || []);
    } catch (error) {
      toast({
        title: 'Error loading mentorship sessions',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditingSession(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (session) => {
    setEditingSession(session);
    setForm({
      mentor_therapist_id: session.mentor_therapist_id,
      mentee_therapist_id: session.mentee_therapist_id,
      session_date: session.session_date,
      session_time: session.session_time?.slice(0, 5) || '',
      hours: String(session.hours),
      notes: session.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.mentor_therapist_id || !form.mentee_therapist_id || !form.session_date || !form.hours) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in mentor, mentee, date, and hours',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (form.mentor_therapist_id === form.mentee_therapist_id) {
      toast({
        title: 'Invalid selection',
        description: 'Mentor and mentee must be different therapists',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const hours = parseFloat(form.hours) || 0;
    const therapistAmount = hours * MENTORSHIP_HOURLY_RATE;

    const payload = {
      mentor_therapist_id: form.mentor_therapist_id,
      mentee_therapist_id: form.mentee_therapist_id,
      session_date: form.session_date,
      session_time: form.session_time || null,
      hours,
      hourly_rate: MENTORSHIP_HOURLY_RATE,
      therapist_amount: therapistAmount,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };

    try {
      setIsSubmitting(true);
      if (editingSession) {
        const { error } = await supabase
          .from('mentorship_sessions')
          .update(payload)
          .eq('id', editingSession.id);
        if (error) throw error;
        toast({ title: 'Mentorship session updated', status: 'success', duration: 3000 });
      } else {
        const { error } = await supabase.from('mentorship_sessions').insert([payload]);
        if (error) throw error;
        toast({ title: 'Mentorship session added', status: 'success', duration: 3000 });
      }

      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (session) => {
    if (!window.confirm('Delete this mentorship session?')) return;

    try {
      const { error } = await supabase.from('mentorship_sessions').delete().eq('id', session.id);
      if (error) throw error;
      toast({ title: 'Deleted', status: 'success', duration: 3000 });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    }
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between">
        <Heading size="md">Mentorship</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>
          Add Session
        </Button>
      </HStack>

      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Time</Th>
              <Th>Mentor</Th>
              <Th>Mentee</Th>
              <Th isNumeric>Hours</Th>
              <Th isNumeric>Amount</Th>
              <Th>Notes</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sessions.map((session) => (
              <Tr key={session.id}>
                <Td>{new Date(session.session_date).toLocaleDateString()}</Td>
                <Td>{session.session_time?.slice(0, 5) || '—'}</Td>
                <Td>{session.mentor?.name}</Td>
                <Td>{session.mentee?.name}</Td>
                <Td isNumeric>{session.hours}</Td>
                <Td isNumeric>{formatCurrency(session.therapist_amount)}</Td>
                <Td>{session.notes || '—'}</Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton
                      icon={<EditIcon />}
                      size="xs"
                      aria-label="Edit"
                      onClick={() => openEdit(session)}
                    />
                    <IconButton
                      icon={<DeleteIcon />}
                      size="xs"
                      aria-label="Delete"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleDelete(session)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
            {sessions.length === 0 && (
              <Tr>
                <Td colSpan={8} textAlign="center" color="gray.500">
                  No mentorship sessions recorded
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingSession ? 'Edit Mentorship Session' : 'Add Mentorship Session'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Mentor</FormLabel>
                <Select
                  placeholder="Select mentor"
                  value={form.mentor_therapist_id}
                  onChange={(e) => setForm({ ...form, mentor_therapist_id: e.target.value })}
                >
                  {therapists.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Mentee</FormLabel>
                <Select
                  placeholder="Select mentee"
                  value={form.mentee_therapist_id}
                  onChange={(e) => setForm({ ...form, mentee_therapist_id: e.target.value })}
                >
                  {therapists
                    .filter((t) => t.id !== form.mentor_therapist_id)
                    .map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </Select>
              </FormControl>
              <HStack w="100%">
                <FormControl isRequired>
                  <FormLabel>Date</FormLabel>
                  <Input
                    type="date"
                    value={form.session_date}
                    onChange={(e) => setForm({ ...form, session_date: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Time</FormLabel>
                  <Input
                    type="time"
                    value={form.session_time}
                    onChange={(e) => setForm({ ...form, session_time: e.target.value })}
                  />
                </FormControl>
              </HStack>
              <FormControl isRequired>
                <FormLabel>Hours</FormLabel>
                <NumberInput
                  min={0.5}
                  step={0.5}
                  value={form.hours}
                  onChange={(value) => setForm({ ...form, hours: value })}
                >
                  <NumberInputField />
                </NumberInput>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Amount: {formatCurrency((parseFloat(form.hours) || 0) * MENTORSHIP_HOURLY_RATE)}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={isSubmitting}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

const ReceptionistRequestsOthers = ({ LoadingComponent }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading && LoadingComponent) {
    return <LoadingComponent />;
  }

  return (
    <Box>
      <Heading size="lg" mb={4}>Requests & Others</Heading>
      <Tabs variant="enclosed" colorScheme="blue" isLazy>
        <TabList>
          <Tab>Initial Evaluations</Tab>
          <Tab>Progress Reports</Tab>
          <Tab>Mentorship</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <InitialEvaluationsTab />
          </TabPanel>
          <TabPanel px={0}>
            <ProgressReportsTab />
          </TabPanel>
          <TabPanel px={0}>
            <MentorshipTab />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default ReceptionistRequestsOthers;
